import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextInput from '../text-input/TextInput';
import { AddTablePrompt } from './grid-input-prompts/GridInputPrompts';
import { GridInputControls } from './grid-input-controls/GridInputControls';
import createConfirmAlert from '../../../../shared/functions/createConfirmAlert';
import classNames from 'classnames';
import './GridInput.css';

export default class GridInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      addTablePromptShowing: false,
      columns: this.calcColumns(this.props.existingContentArray),
      inputValues: (this.props.existingContentArray) ? this.props.existingContentArray : [],
      rows: this.props.existingContentArray && this.props.existingContentArray.length
    };
  }
  
  componentWillReceiveProps(newProps) {
    // For case where user is going from edit-mode to read-only-mode (like onCancel)
    if ( 
      newProps.existingContentArray !== this.props.existingContentArray || 
      (this.props.isEditable && 
      !newProps.isEditable)
    ) {
      
      // Closes all open panels when the editor is going into read-only mode
      let closePanels = (this.props.isEditable && !newProps.isEditable) && {
        addTablePromptShowing: false
      };
      
      this.setState({ 
        columns: this.calcColumns(newProps.existingContentArray),
        inputValues: newProps.existingContentArray,
        rows: newProps.existingContentArray && newProps.existingContentArray.length,
        ...closePanels
      });
    }
  }
  
  // Takes number value and converts it into excel-column-format letters, so hitting AA once A-Z are used up, etc
  numberToLetter = (number) => {
    return (
      ((number >= 26) ? 
        this.numberToLetter((number / 26 >> 0) - 1) : 
        ''
      ) + 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[number % 26]
    );
  }
  
  // Grabs the values from the text inputs of each cell to return, and updates the state of the component to match
  getValue = () => {
    let gridData = [];

    for (let i = 0; i < this.state.rows; ++i) {
      let gridDataRow = [];

      for (let j = 0; j < this.state.columns; ++j) {
        gridDataRow.push(
          this[this.props.id + "-row" + i + "column" + j].getValue()
        );
      }
      
      gridData.push(gridDataRow);
    }
    
    this.setState({ inputValues: gridData });
    
    return (gridData.length) ? gridData : null;
  }
  
  // Shows and hides the add table prompt
  toggleAddTablePrompt = () => {
    this.setState((prevState) => {
      
      let addTablePromptShowing = !prevState.addTablePromptShowing;
      
      return { addTablePromptShowing };
    });
  }
  
  // Takes the row and column numbers passed from the add table prompt and creates empty table data
  confirmAddTable = (rowCount, columnCount) => {
    this.toggleAddTablePrompt();
    
    const rows = parseInt(rowCount, 10);
    const columns = parseInt(columnCount, 10);
    
    let inputValues = [];
    for (let i = 0; i < rows; ++i) {
      inputValues.push(new Array(columns).fill(""));
    }

    this.setState({ rows, columns, inputValues });
  }
  
  // Adds a new column to the table data
  addColumn = () => {
    let inputValues = this.getValue();
    
    this.setState((prevState) => {
      const columns = prevState.columns + 1;

      for (let i = 0; i < prevState.rows; ++i) {
        inputValues[i].push("");
      }
      
      return { columns, inputValues };
    });
  }
  
  // Adds a new row to the table data
  addRow = () => {
    let inputValues = this.getValue();
    
    this.setState((prevState) => {
      const rows = prevState.rows + 1;

      inputValues.push(new Array(prevState.columns).fill(""));
      
      return { rows, inputValues };
    });
  }
  
  // Creates a confirmation message for deleting a row or column, where proper delete function will be called if accepted
  confirmDelete = (index, rowOrColumn) => {
    let confirmFunction = (rowOrColumn === 'row') ?
      this.deleteRow :
      this.deleteColumn;
    
    let displayIndex = (rowOrColumn === 'column') ?
      this.numberToLetter(index) :
      index + 1;
    
    let confirmAlertObject = {
      title: 'Confirm Action',
      message: `Are you sure you want to delete ${rowOrColumn} ${displayIndex}?`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {}
        },
        {
          label: 'Confirm',
          onClick: () => confirmFunction(index)
        }
      ]
    }
    
    createConfirmAlert(confirmAlertObject);
  }
  
  // Deletes the column with the specified 0-based index
  deleteColumn = (index) => {
    if (this.state.columns === 1) {
      this.deleteTable();
    }
    else {
      let inputValues = this.getValue();
      
      this.setState((prevState) => {
        const columns = prevState.columns - 1;
        
        inputValues.forEach((row) => row.splice(index, 1));

        return {columns, inputValues};
      });
    }
  }
  
  // Deletes the row with the specified 0-based index
  deleteRow = (index) => {
    let inputValues = this.getValue();
    
    this.setState((prevState) => {
      const rows = prevState.rows - 1;

      inputValues.splice(index, 1);
      
      return {rows, inputValues};
    });
  }
  
  // Removes the current table and default everything
  deleteTable = () => {
    this.setState({ 
      rows: 0, 
      columns: 0, 
      inputValues: [], 
      tempInputValues: null 
    });
  }
  
  // Figures out what the column count should be based on the longest row of the data
  calcColumns = (data) => {
    return data && data.reduce((maxLength, current, index) => {
        if (current.length > maxLength){
          return current.length;
        }
        return maxLength;
      }, 0);
  }
  
  render() {
    const label = (this.props.label) ?
      <label htmlFor={this.props.id}>
        {this.props.label}
      </label> :
      null;
    
    let controlButtonOptions = null;
    
    if (this.props.isEditable) {
      controlButtonOptions = (!this.state.inputValues || !this.state.inputValues.length) ? [
        {
          label: "Add Table",
          onClick: this.toggleAddTablePrompt
        }
      ] : 
      [
        {
          label: "Add Column",
          onClick: this.addColumn
        },
        {
          label: "Add Row",
          onClick: this.addRow
        },
        {
          label: "Delete Table",
          onClick: this.deleteTable
        }
      ];
    }
    
    let toolbar = (this.props.isEditable) ?
      <span>
        <GridInputControls
          options= {controlButtonOptions}
        />
        <AddTablePrompt 
          handleCancel={this.toggleAddTablePrompt}
          handleSubmit={this.confirmAddTable}
          formId={this.props.id + "-add-table-prompt"}
          isShowing={this.state.addTablePromptShowing}
        />
      </span>:
      null;
    
    let tableRows = [];
    
    if (this.state.rows && this.state.columns && this.state.inputValues.length) {
      for (let i = 0; i < this.state.rows; ++i) {
        // For read-only mode, hides th row if there's no values in it
        if (
          i !== 0 || 
          this.props.isEditable || 
          this.state.inputValues[i].some(val => val)
        ) {
          let tableRowElements = [];
    
          if (i === 0) { 
            tableRows.push(
              <tr key={i}>
                {[
                  <td className="grid-input__index-blank" key="blank-cell"></td>, 
                  ...Array.from(
                    Array(this.state.columns).keys(), 
                    (cell) => {
                      return <td 
                        className="grid-input__index-cell" 
                        key={cell} 
                        onClick={() => this.confirmDelete(cell, 'column')}
                      >
                        {this.numberToLetter(cell)}
                      </td>
                    }
                  )
                ]}
              </tr>
            );
          }

          for (let j = 0; j < this.state.columns; ++j) {
            let existingCellContent = (this.state.inputValues[i] && 
            j <= this.state.inputValues[i].length - 1) ?
              this.state.inputValues[i][j] :
              null;

            let CellType = (i === 0) ? "th" : "td";

            tableRowElements.push(
              <CellType key={"row" + i + "column" + j}>
                <TextInput
                  autoComplete={false}
                  className="grid-input__text-input"
                  existingContent={existingCellContent}
                  id={this.props.id + "-row" + i + "column" + j}
                  isEditable={this.props.isEditable}
                  ref={instance => {this[this.props.id + "-row" + i + "column" + j] = instance; }}
                />
              </CellType>
            );
          }

          tableRows.push(
            <tr key={"row" + i}>
              <td className="grid-input__index-cell" onClick={() => this.confirmDelete(i, 'row')}>{i + 1}</td>
              {tableRowElements}
            </tr>
          );
        }
      }
    }
    
    let table = (this.state.rows && this.state.columns && this.state.inputValues.length) ?
      <table className="grid-input__table">
        <tbody>{tableRows}</tbody>
      </table> :
      null;
    
    let verticalSpacingClass = this.props.verticalSpacing === "compact" ? "field--compact" : "field";
    
    let readOnlyToggleClass = classNames(verticalSpacingClass , 'grid-input', {
      'read-only': !this.props.isEditable
    });

    return (
      <div className={readOnlyToggleClass} id={this.props.id}>
        {label}
        {toolbar}
        {table}
      </div>
    );
  }
}
    
GridInput.defaultProps = {
  isEditable: true
};
            
GridInput.propTypes = {
  existingContentArray: PropTypes.arrayOf(PropTypes.array),
  id: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
  verticalSpacing: PropTypes.string
};

